export type DataLayerEvent = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;

export function pushDataLayer(event: DataLayerEvent): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);

  if (process.env.NODE_ENV === 'development') {
    console.debug('[analytics]', event);
  }
}

export function trackVirtualPageView(pagePath: string, pageTitle?: string): void {
  pushDataLayer({
    event: 'virtual_page_view',
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
}

export function trackCtaClick(details: {
  clickText: string;
  clickUrl?: string;
  clickType: 'internal' | 'external' | 'email' | 'button';
  pagePath: string;
  pageSection?: string;
}): void {
  pushDataLayer({
    event: 'cta_click',
    click_text: details.clickText,
    click_url: details.clickUrl ?? '',
    click_type: details.clickType,
    page_path: details.pagePath,
    page_section: details.pageSection ?? '',
  });
}

export function trackScrollDepth(scrollPercentage: number, pagePath: string): void {
  pushDataLayer({
    event: 'scroll_depth',
    scroll_percentage: scrollPercentage,
    page_path: pagePath,
  });
}

export function getScrollThresholds(): readonly number[] {
  return SCROLL_THRESHOLDS;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, 120);
}

function getClickTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;

  return target.closest<HTMLElement>(
    'a[href], button, [role="button"], input[type="submit"], input[type="button"], .btn',
  );
}

function resolveClickUrl(element: HTMLElement): string | undefined {
  if (element instanceof HTMLAnchorElement) return element.href;
  if (element instanceof HTMLInputElement) return element.form?.action;
  return undefined;
}

function resolveClickType(url: string | undefined): 'internal' | 'external' | 'email' | 'button' {
  if (!url) return 'button';
  if (url.startsWith('mailto:')) return 'email';

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) return 'internal';
    return 'external';
  } catch {
    return 'button';
  }
}

function resolveClickText(element: HTMLElement): string {
  const customLabel = element.getAttribute('data-analytics-label');
  if (customLabel) return normalizeText(customLabel);

  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return normalizeText(ariaLabel);

  return normalizeText(element.textContent || 'unknown');
}

function resolvePageSection(element: HTMLElement): string | undefined {
  const section = element.closest<HTMLElement>('[data-analytics-section]');
  if (section) return section.getAttribute('data-analytics-section') ?? undefined;

  const landmark = element.closest('header, footer, main, nav, section');
  if (landmark instanceof HTMLElement) {
    if (landmark.tagName === 'HEADER') return 'header';
    if (landmark.tagName === 'FOOTER') return 'footer';
    if (landmark.tagName === 'NAV') return 'nav';
    if (landmark.className) return landmark.className.split(/\s+/)[0];
  }

  return undefined;
}

export function trackClickFromElement(target: EventTarget | null, pagePath: string): void {
  const element = getClickTarget(target);
  if (!element) return;

  const clickUrl = resolveClickUrl(element);

  trackCtaClick({
    clickText: resolveClickText(element),
    clickUrl,
    clickType: resolveClickType(clickUrl),
    pagePath,
    pageSection: resolvePageSection(element),
  });
}

export function getScrollDepthPercent(): number {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop;
  const scrollHeight = doc.scrollHeight - doc.clientHeight;

  if (scrollHeight <= 0) return 100;

  return Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
}
